import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useCollectionBindingOverrides } from '@vibesites/runtime/react';
import type { CollectionBindingOverridesMap } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  MobileGalleryCarousel,
  type GalleryImage,
} from './_galleryShared';
import { sortGalleryImages, type GalleryGroup } from '../utils/gallerySort';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

/**
 * Filterable photo gallery — renders a single grid (with its own lightbox)
 * whose contents switch between "All", "Community", and "Apartment" via a
 * row of filter tabs. Two collection bindings drive the categories: photos
 * whose `title` is exactly "Community" and photos whose `title` is exactly
 * "Apartment". Photos are ordered by content (exterior → outdoor community →
 * indoor community → apartment rooms) unless an editor has saved any change
 * to a binding (order, hidden, added photos, or limit), in which case that
 * list renders exactly as edited and no photos move into or out of it. The
 * "All" tab is the Community list followed by the Apartment list. Duplicate
 * photos (same image URL) are hidden everywhere — only the first is shown.
 */
type GalleryFilter = 'all' | 'community' | 'apartment';

const FILTER_TABS: { key: GalleryFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'community', label: 'Community' },
  { key: 'apartment', label: 'Apartment' },
];

const APARTMENT_BINDING = 'galleryapartments.images';
const COMMUNITY_BINDING = 'galleryapartments.communityImages';

/** True when the editor has saved any change to this binding (unset `null`s don't count). */
function hasEditorChanges(overrides: CollectionBindingOverridesMap, bindingPath: string): boolean {
  return Object.entries(overrides).some(
    ([key, o]) =>
      (key === bindingPath || key.endsWith(`::${bindingPath}`)) &&
      !!o &&
      (o.orderedIds != null ||
        o.hiddenIds != null ||
        !!o.ordering?.length ||
        !!o.hiddenIndices?.length ||
        !!o.customItems?.length ||
        o.limit != null),
  );
}

function withImageUrl(images: GalleryImage[]): GalleryImage[] {
  return images
    .map((img) => (img.image ? img : { ...img, image: img.original_url || '' }))
    .filter((img) => img.image);
}

/** Identity used for duplicate detection — CDN query params (sizing, cache-busting) don't make a new photo. */
function imageKey(img: GalleryImage): string {
  return img.image.split(/[?#]/)[0].trim().toLowerCase();
}

/** Keeps only the first occurrence of each photo, preserving order. */
function dedupeImages(images: GalleryImage[]): GalleryImage[] {
  const seen = new Set<string>();
  return images.filter((img) => {
    const key = imageKey(img);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function GalleryApartments() {
  const rawApartmentImages = useCollectionBinding<GalleryImage>(APARTMENT_BINDING, {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
  });

  const rawCommunityImages = useCollectionBinding<GalleryImage>(COMMUNITY_BINDING, {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
  });

  const ctaVars = useCtaColorVars();
  const overrides = useCollectionBindingOverrides();
  const apartmentEdited = hasEditorChanges(overrides, APARTMENT_BINDING);
  const communityEdited = hasEditorChanges(overrides, COMMUNITY_BINDING);

  const { communityImages, apartmentImages } = useMemo(() => {
    const community = dedupeImages(withImageUrl(rawCommunityImages));
    const apartment = dedupeImages(withImageUrl(rawApartmentImages));
    const tag = (list: GalleryImage[], group: GalleryGroup) =>
      list.map((item) => ({ item, group }));
    const sorted = sortGalleryImages(
      [
        ...(communityEdited ? [] : tag(community, 'community')),
        ...(apartmentEdited ? [] : tag(apartment, 'apartment')),
      ],
      { allowCrossGroup: !communityEdited && !apartmentEdited },
    );
    return {
      communityImages: communityEdited ? community : sorted.community,
      apartmentImages: apartmentEdited ? apartment : sorted.apartment,
    };
  }, [rawCommunityImages, rawApartmentImages, apartmentEdited, communityEdited]);

  const [filter, setFilter] = useState<GalleryFilter>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const allImages = useMemo(
    () => dedupeImages([...communityImages, ...apartmentImages]),
    [communityImages, apartmentImages],
  );

  const displayedImages =
    filter === 'apartment'
      ? apartmentImages
      : filter === 'community'
        ? communityImages
        : allImages;

  // Switching tabs re-indexes the grid, so drop any open lightbox selection.
  const handleFilter = (next: GalleryFilter) => {
    setFilter(next);
    setSelectedIndex(null);
  };

  if (allImages.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white" style={ctaVars}>
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <div className="gallery-tabs" role="tablist" aria-label="Filter gallery photos">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={filter === tab.key}
              className={`gallery-tab${filter === tab.key ? ' is-active' : ''}`}
              onClick={() => handleFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {displayedImages.length > 0 ? (
          <>
            {/* Tablet / desktop: full-bleed responsive grid — every photo
               renders as an identical square tile separated by a fixed gap. */}
            <div className="hidden sm:block">
              <div className="gallery-grid">
                {displayedImages.map((img, index) => (
                  <div
                    key={img.image || index}
                    className="gallery-grid__item gallery-item group"
                    onClick={() => setSelectedIndex(index)}
                  >
                    <VibesitesImage
                      url={img.image}
                      alt={img.alt_text || img.title || ''}
                      fill
                      className="transition-transform duration-500 group-hover:scale-110"
                      sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile: single-image swipeable slideshow */}
            <div className="sm:hidden">
              <MobileGalleryCarousel
                key={filter}
                images={displayedImages}
                onSelect={setSelectedIndex}
                showDots={false}
              />
            </div>
          </>
        ) : (
          <p className="gallery-tabs__empty font-body">
            No photos in this category yet.
          </p>
        )}
      </div>

      <GalleryLightbox
        images={displayedImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.75rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 0.5rem 1.35rem;
          border: 1px solid var(--vibe-color-primary);
          border-radius: 0;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .gallery-tab:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, #000);
        }
        .gallery-tab.is-active {
          background: var(--cta-bg, var(--vibe-color-secondary));
          border-color: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary));
        }
        .gallery-tab.is-active:hover {
          background: var(--cta-bg, var(--vibe-color-secondary));
        }

        /* Mobile: stack the filter tabs vertically, each full-width */
        @media (max-width: 639px) {
          .gallery-tabs {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
            padding: 0 1rem;
          }
          .gallery-tab {
            width: 100%;
            padding: 1rem 1.35rem;
          }
        }

        .gallery-tabs__empty {
          text-align: center;
          color: var(--vibe-color-text-muted);
          font-size: 1.05rem;
          padding: 2.5rem 0;
        }

        /* Full-bleed photo grid spanning the whole viewport, with one fixed
           gap between every tile (and at the viewport edges) at all
           breakpoints and on every tab. The grid lives inside the
           width-constrained container, so break it out to 100vw and center it
           on the viewport regardless of the parent's width. */
        .gallery-grid {
          --gallery-gap: 0.75rem;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--gallery-gap);
          padding: 0 var(--gallery-gap);
          box-sizing: border-box;
          width: 100vw;
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
        }
        @media (min-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .gallery-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .gallery-grid__item {
          position: relative;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          cursor: pointer;
          /* Grid items default to min-height:auto, which lets a tall image's
             intrinsic height stretch its row past the 1:1 square. Pin the
             minimums to 0 so the aspect-ratio always wins and every tile is
             the same size. */
          min-width: 0;
          min-height: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Apartment Gallery',
  tokens: {},
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Apartment" appear under the Apartment (and All) filter, auto-ordered by room (kitchen, island, living, dining, bedroom, bath, closet, laundry, patio). Any edit here (reorder, hide, add, or limit) replaces the automatic order.',
    },
    'galleryapartments.communityImages': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Community" appear under the Community (and All) filter, auto-ordered exterior, then outdoor, then indoor amenities. Any edit here (reorder, hide, add, or limit) replaces the automatic order.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
